/**
 * ComposerActionButton — the composer's send↔Stop action control (bead
 * terra-design-v2-chat-yub.6, spec §4).
 *
 * Ported from `clayground-sc-terra-flow-panels/projects/sc-terra-flow/src/chat/ComposerActionButton.tsx`,
 * itself lifted verbatim from clay-base's
 * `apps/frontend/src/components/Sculptor/SculptorChatInput/ComposerActionButton.tsx` (zero coupling
 * to clay-base's Sculptor/GTM Agent state) — see that file's own header comment. Two changes from
 * the prototype: Glaze is imported by real subpath (`@clay-run/glaze/button`, `/iconbutton`) per
 * coding-standards.md rather than the prototype's barrel import, and the glyphs route through
 * `FLOW_ICONS` (`components/flow/icons.ts`'s `arrowUp`/`stop` keys, added by this bead) rather than
 * a direct `@phosphor-icons/react` import — `.oxlintrc.json:313-327` bans the latter under
 * `shell-next/**`.
 *
 * `isStopAction` is the one predicate this whole unit exists to get right: while streaming, the
 * button reads Stop UNLESS the composer both allows submitting mid-stream AND isn't currently
 * blocked/submitting. Terra's own composer (`ChatComposerV2`) always passes
 * `canSubmitWhileStreaming={false}` (brief §9 #7 is deferred), so in practice `isStopAction` is
 * simply `isStreaming` here — but this component's own contract (and its test suite) verifies the
 * general predicate, independent of that caller choice, per the bead's spec §4.
 */
import clsx from 'clsx';
import type { MouseEventHandler } from 'react';
import { Button } from '@clay-run/glaze/button';
import { IconButton } from '@clay-run/glaze/iconbutton';
import { FLOW_ICONS } from '../../icons';

export interface ComposerActionButtonProps {
  compact: boolean;
  isStreaming: boolean;
  canSubmitWhileStreaming: boolean;
  isSubmitting: boolean;
  sendDisabled: boolean;
  submitLabel?: string;
  onSubmit: () => void;
  onStop?: () => void;
  onMouseDown?: MouseEventHandler<HTMLButtonElement>;
}

const iconTransition =
  'col-start-1 row-start-1 transition-[opacity,scale,filter] duration-200 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-opacity';
const hiddenIcon = 'opacity-0 motion-safe:scale-95 motion-safe:blur-[4px]';

export function ComposerActionButton({
  compact,
  isStreaming,
  canSubmitWhileStreaming,
  isSubmitting,
  sendDisabled,
  submitLabel = 'Send',
  onSubmit,
  onStop,
  onMouseDown,
}: ComposerActionButtonProps) {
  const isStopAction = isStreaming && (!canSubmitWhileStreaming || (sendDisabled && !isSubmitting));
  const pending = !isStopAction && isSubmitting;
  const disabled = !isStopAction && sendDisabled;
  const label = isStopAction ? 'Stop' : submitLabel;
  const onClick = isStopAction ? onStop : onSubmit;

  if (!compact) {
    return (
      <IconButton
        onClick={onClick}
        onMouseDown={onMouseDown}
        aria-label={label}
        icon={isStopAction ? FLOW_ICONS.stop : FLOW_ICONS.arrowUp}
        variant={isStopAction ? 'secondary' : 'primary'}
        size="md"
        disabled={disabled}
        loading={pending}
        className={
          isStopAction
            ? 'text-content-primary [--btn-bg:var(--color-bg-grey)] [--btn-hover-bg:var(--color-bg-grey-hover)]'
            : undefined
        }
        iconClassName="size-5!"
      />
    );
  }

  return (
    <Button
      onClick={onClick}
      onMouseDown={onMouseDown}
      aria-label={label}
      aria-busy={pending || undefined}
      variant="primary"
      size="content"
      disabled={disabled || pending}
      focusableWhenDisabled={pending}
      className={clsx(
        'size-7 shrink-0 rounded-md',
        'transition-[background-color,scale] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]',
        'motion-safe:enabled:active:scale-96 motion-reduce:transition-[background-color]',
        'data-disabled:opacity-100',
        !pending && [
          'data-disabled:[--btn-disabled-bg:var(--color-bg-secondary)]',
          'data-disabled:[--btn-disabled-text:var(--color-content-disabled)]',
        ],
      )}
    >
      <ComposerActionIcon showProgress={isStopAction || pending} />
    </Button>
  );
}

function ComposerActionIcon({ showProgress }: { showProgress: boolean }) {
  const ArrowUpIcon = FLOW_ICONS.arrowUp;
  return (
    <span aria-hidden className="grid size-4.5 place-items-center">
      <ArrowUpIcon size={14} weight="bold" className={clsx(iconTransition, showProgress && hiddenIcon)} />
      <svg viewBox="0 0 18 18" fill="none" className={clsx(iconTransition, 'size-4.5', !showProgress && hiddenIcon)}>
        <circle cx="9" cy="9" r="7.25" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1.5" />
        <circle
          cx="9"
          cy="9"
          r="7.25"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeDasharray="11.4 34.2"
          className={clsx('origin-center', showProgress && 'motion-safe:animate-[spin_850ms_linear_infinite]')}
        />
        <rect x="5.5" y="5.5" width="7" height="7" rx="1.5" fill="currentColor" />
      </svg>
    </span>
  );
}
